import React, { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import { ActivateFeatureButton, CardTableRow, CardTableRows, CardTableShell, CardTableToolbar, FlexContainer, LottieLoadingIndicator, PortalSearchInput, PortalTableActions, PortalTableControls, Toast } from "src/components";
import { InputSelector, ActionButton, TextLabel } from "../../components/style";
import TableStatus from "./TableStatus";
import TableName from "./TableName";
import TableAnalytics from "./TableAnalytics";
import TableAudience from "./TableAudience";
import { EmailCampaignsContext } from "../../EmailCampaignsContext";
import { getDraftEmails, getScheduledEmails, getSentEmails } from "../../api/emailCampaignsApi";
import { getAudiences } from "../../api/audienceApi";
import { DRAFT_SCHEDULED_ACTIONS, EMAIL_STATUS, SIDEBAR_LIST } from "../../constants";
import DeleteDialog from "../../components/DeleteDialog";
import NoData from "./NoData";
import DuplicateDialog from "../../components/DuplicateDialog";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { resendEmailCampaign } from "../../api/newCampaignApi";

// Email status values which might change after a while without manual action
const transientEmailStatuses = [EMAIL_STATUS.SCHEDULED, EMAIL_STATUS.SENDING, EMAIL_STATUS.QUEUED];

export default function EmailCampaignsTable(props) {
    const { status } = props;
    const {
        startNewEmail,
        startUpdatingEmail,
        showDeleteDialog,
        startDeletingCampaign,
        dismissDeleteDialog,
        onDeleteEmail,
        deleteIsLoading,
        selectedCampaign,
        setSelectedCampaign,
        setSelectedEmailsToDelete,
        selectedEmailsToDelete,
        handleDeleteSelectedCampaigns,
        setCurrentSidebar
    } = useContext(EmailCampaignsContext);

    const [audiences, setAudiences] = useState([]);
    const [emailsIsFetching, setEmailsIsFetching] = useState(false);
    const [emailsList, setEmailsList] = useState([]);
    const [allEmails, setAllEmails] = useState([]);
    const [selectedAction, setSelectedAction] = useState({});
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [hasScheduledOrDraft, setHasScheduledOrDraft] = useState(false);

    const tableHeadings = [
        { id: "campaignName", name: "Name", size: "160px" },
        { id: "emailStatus", name: "Status", size: "120px" },
        { id: "sentAt", name: "Sent", size: "100px" },
        { id: "scheduleAt", name: "Scheduled", size: "100px" },
        { id: "audience", name: "Audience", size: "120px" },
        { id: "analytics", name: "Analytics", size: "120px" },
        { id: "actions", name: "Action", size: "100px" },
    ];

    const fetchDataByCurrentStatus = async () => {
        switch (status) {
            case SIDEBAR_LIST.SENT:
                return await getSentEmails();
            case SIDEBAR_LIST.SCHEDULED:
                return await getScheduledEmails();
            case SIDEBAR_LIST.DRAFT:
                return await getDraftEmails();
        }
    };

    const getAllEmails = useCallback(async () => {
        if (!status) return;

        setEmailsIsFetching(true);

        try {
            const audienceResponse = await getAudiences();
            const response = await fetchDataByCurrentStatus();
            setAudiences(audienceResponse.customAudiences);
            setEmailsList(response.emails);
            setAllEmails(response.emails);
            const hasData =
                (response?.count?.draft ?? 0) > 0 ||
                (response?.count?.scheduled ?? 0) > 0;
            setHasScheduledOrDraft(hasData);
        } catch (error) {
            console.error("Error:", error);
            Toast.error("Error occurred while fetching emails.");
        } finally {
            setEmailsIsFetching(false);
        }
    }, [status, deleteIsLoading]);

    useEffect(() => {
        getAllEmails();
    }, [getAllEmails]);

    // Polling for status updates on emails in a transient status (will change automatically after some time)
    useEffect(() => {
        let isCancelled = false;

        // Poll email campaigns until nothing has a transient status
        const pollEmailCampaigns = async (previousUuids) => {

            const task = new Promise((resolve) => {
                (async () => {
                    let synced = false;

                    while (!synced && !isCancelled) {
                        try {
                            const response = await fetchDataByCurrentStatus();
                            const transientEmails = (response?.emails ?? []).filter(email =>
                                transientEmailStatuses.includes(email.emailStatus)
                            );
                            const transientEmailUuids = transientEmails.map(email => email.uuid);
                            if (transientEmailUuids.length !== previousUuids.length) {
                                synced = true;
                            } else {
                                await new Promise(r => setTimeout(r, 5000));
                            }
                        } catch (err) {
                            console.error("Error polling emails:", err);
                            synced = true;
                        }
                    }

                    if (!synced) {
                        console.warn("Polling for emails error.");
                    }

                    resolve();
                })();
            });

            await task;

            if (!isCancelled) {
                // Do not await this, as we need this task to end before fetching emails completes.
                // That way, the task will restart if (and only if) it is needed.
                getAllEmails();
            }
        };

        const transientEmails = emailsList.filter(email =>
            transientEmailStatuses.includes(email.emailStatus)
        );

        if (transientEmails.length > 0) {
            const transientEmailUuids = transientEmails.map(email => email.uuid);
            pollEmailCampaigns(transientEmailUuids);
        }

        return () => {
            isCancelled = true;
        };
    }, [emailsList]);

    const onChangeAction = (campaign, value) => {
        setSelectedCampaign(campaign);
        setSelectedAction((prev) => ({
            ...prev,
            [campaign.uuid]: value,
        }));

        if (value === DRAFT_SCHEDULED_ACTIONS.UPDATE) {
            startUpdatingEmail(campaign);
        }

        if (value === DRAFT_SCHEDULED_ACTIONS.DUPLICATE) {
            setShowDuplicateDialog(true);
        }

        if (value === DRAFT_SCHEDULED_ACTIONS.DELETE) {
            startDeletingCampaign(campaign);
        }
    };

    const emailCampaignIsSelected = (uuid) =>
        selectedEmailsToDelete.some(u => u.uuid === uuid);

    const onSelectEmailCampaign = (campaign) => {
        setSelectedEmailsToDelete(prev => {
            const isAlreadySelected = prev.some(q => q.uuid === campaign.uuid);
            if (isAlreadySelected) {
                return prev.filter(q => q.uuid !== campaign.uuid);
            } else {
                return [...prev, campaign];
            }
        });
    };

    const onDelete = () => {
        onDeleteEmail();
    };

    const getTableHeadings = (status) => {
        return tableHeadings.filter(({ id }) => {
            if (status === SIDEBAR_LIST.SENT) {
                return id !== "scheduleAt";
            }

            if (status === SIDEBAR_LIST.SCHEDULED || status === SIDEBAR_LIST.DRAFT) {
                return id !== "sentAt";
            }

            return true;
        });
    };

    const onSearchItem = (e) => {
        const value = e.target.value.toLowerCase().trim();
        if (!value) {
            setEmailsList(allEmails);
            return;
        }

        const filtered = allEmails?.filter((item) =>
            item?.campaignName?.toLowerCase().includes(value)
        );

        setEmailsList(filtered);
    };

    const handleResendCampaign = async (campaignUuid) => {
        setEmailsIsFetching(true);
        try {
            await resendEmailCampaign(campaignUuid);
            Toast.success("Email resent successfully");
            await getAllEmails();
        } catch (err) {
            console.error(err);
            Toast.error("Failed to resend email");
        } finally {
            setEmailsIsFetching(false);
        }
    };

    const filteredHeadings = getTableHeadings(status);

    return (
        <CardTableShell minHeight="48rem">
            <CardTableToolbar>
                <PortalTableControls>
                    <PortalSearchInput
                        placeholder="Search Campaign"
                        type="text"
                        onChange={(e) => onSearchItem(e)}
                    />
                    {selectedEmailsToDelete.length > 0 ?
                        deleteIsLoading ? (
                            <LoadingIndicatorButton width="84px" height="39px">
                                <LottieLoadingIndicator
                                    height="2rem"
                                    width="2rem"
                                    margin="5px"
                                    containerHeight="2rem"
                                />
                            </LoadingIndicatorButton>
                        ) : (
                            <ActionButton
                                width="140px"
                                height="39px"
                                border="1.5px solid #FF3B3B"
                                color="#FF3B3B"
                                backgroundColor="#FFF5F5"
                                onClick={() => handleDeleteSelectedCampaigns()}
                            >
                                Delete Campaign
                            </ActionButton>)
                        : null}
                </PortalTableControls>
                <PortalTableActions>
                    <ActivateFeatureButton
                        width="184px"
                        height="39px"
                        onClick={startNewEmail}
                        disabled={deleteIsLoading}
                    >
                        New Campaign
                    </ActivateFeatureButton>
                </PortalTableActions>
            </CardTableToolbar>

            {emailsIsFetching ? (
                <FlexContainer
                    justifyContent={emailsIsFetching && "center"}
                    alignItems="center"
                    color="#fff"
                    style={{
                        overflowX: "auto",
                        maxWidth: "98.625rem",
                        minHeight: "40rem",
                    }}
                >
                    <LottieLoadingIndicator />
                </FlexContainer>
            ) : (
                <CardTableRows maxHeight="48rem">
                    {!emailsIsFetching && emailsList.length > 0 ? (
                        emailsList.map((campaign, index) => (
                            <CardTableRow
                                selected={emailCampaignIsSelected(campaign.uuid)}
                                key={campaign.uuid || index}
                                minWidth="54rem"
                                columns="auto minmax(0, 1fr)"
                                padding="1rem 0.85rem 1rem 0"
                            >
                                <div style={{ marginLeft: "20px" }}>
                                    <Checkbox
                                        checked={emailCampaignIsSelected(campaign.uuid)}
                                        onClickCallback={() => {
                                            // toggleItemSelection(audience.uuid);
                                            onSelectEmailCampaign(campaign);
                                        }}
                                        radius="3px"
                                        border="1px solid #DBE5ED"
                                        color="#FFFFFF"
                                        position="0% 0%"
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        marginLeft: "20px",
                                        width: "1000px",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    {filteredHeadings.map((heading, index) => {
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    width: heading.size,
                                                    maxWidth: heading.size,
                                                    marginLeft: "10px",
                                                }}
                                            >
                                                <TextLabel fontWeight="bold">
                                                    {heading.name}
                                                </TextLabel>
                                                {heading.id === "campaignName" && (
                                                    <TableName
                                                        campaign={campaign}
                                                    />
                                                )}
                                                {heading.id === "emailStatus" && (
                                                    <TableStatus
                                                        status={campaign.emailStatus}
                                                        campaignUuid={campaign.uuid}
                                                        onResend={handleResendCampaign}
                                                    />
                                                )}
                                                {status === SIDEBAR_LIST.SENT &&
                                                    heading.id === "sentAt" &&
                                                    (
                                                        <TextLabel
                                                            style={{
                                                                wordWrap: "break-word",
                                                                whiteSpace: "normal",
                                                                paddingRight: "10px",
                                                            }}
                                                        >
                                                            {campaign?.sentAt
                                                                ? moment(campaign?.sentAt).format(
                                                                    "Do MMMM, YYYY h:mma"
                                                                )
                                                                : "N/A"}
                                                        </TextLabel>
                                                    )}
                                                {(status === SIDEBAR_LIST.SCHEDULED ||
                                                    status === SIDEBAR_LIST.DRAFT) &&
                                                    heading.id === "scheduleAt" &&
                                                    (
                                                        <TextLabel
                                                            style={{
                                                                wordWrap: "break-word",
                                                                whiteSpace: "normal",
                                                                paddingRight: "10px",
                                                            }}
                                                        >
                                                            {campaign?.scheduleAt
                                                                ? moment(
                                                                    campaign?.scheduleAt
                                                                ).format("Do MMMM, YYYY h:mma")
                                                                : "N/A"}
                                                        </TextLabel>
                                                    )}
                                                {heading.id === "audience" && (
                                                    <TableAudience
                                                        audiences={audiences}
                                                        recipientContacts={campaign?.recipientContacts}
                                                        campaignName={campaign?.campaignName}
                                                        totalRecipients={campaign?.totalRecipients}
                                                        emailStatus={campaign?.emailStatus}
                                                    />
                                                )}
                                                {heading.id === "analytics" && (
                                                    <TableAnalytics
                                                        opens={campaign?.analytics?.opens ?? 0}
                                                        clicks={campaign?.analytics?.clicks ?? 0}
                                                    />
                                                )}
                                                {heading.id === "actions" && (
                                                    <InputSelector
                                                        value={selectedAction[campaign.uuid] ?? "select"}
                                                        onChange={(e) =>
                                                            onChangeAction(
                                                                campaign,
                                                                e.target.value)
                                                        }
                                                        width="91px"
                                                        height="30px"
                                                        size="12px"
                                                        style={{ textTransform: "capitalize" }}
                                                    >
                                                        <option value={"select"}>Select</option>
                                                        {[EMAIL_STATUS.SENT, EMAIL_STATUS.SENDING, EMAIL_STATUS.QUEUED, EMAIL_STATUS.FAILED].includes(status) ? null :
                                                            <option value={DRAFT_SCHEDULED_ACTIONS.UPDATE}>
                                                                {DRAFT_SCHEDULED_ACTIONS.UPDATE}
                                                            </option>}
                                                        <option
                                                            value={
                                                                DRAFT_SCHEDULED_ACTIONS.DUPLICATE
                                                            }
                                                        >
                                                            {DRAFT_SCHEDULED_ACTIONS.DUPLICATE}
                                                        </option>
                                                        <option
                                                            value={DRAFT_SCHEDULED_ACTIONS.DELETE}
                                                        >
                                                            {DRAFT_SCHEDULED_ACTIONS.DELETE}
                                                        </option>
                                                    </InputSelector>
                                                )
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardTableRow>
                        ))
                    ) : (
                        <NoData startNewEmail={startNewEmail} emailStatus={status} hasScheduledOrDraft={hasScheduledOrDraft} />
                    )}
                </CardTableRows>
            )
            }
            {showDeleteDialog && (
                <DeleteDialog
                    title={"Are you sure you want to delete this email campaign?"}
                    subtitle="This action cannot be undone."
                    dismissDialog={dismissDeleteDialog}
                    deleteFunction={onDelete}
                    isLoading={deleteIsLoading}
                    setSelectedAction={setSelectedAction}
                />
            )}

            {selectedCampaign && showDuplicateDialog &&
                <DuplicateDialog
                    campaignUuid={selectedCampaign.uuid}
                    emailSubject={selectedCampaign.emailSubject}
                    headerImageUrl={selectedCampaign.headerImageUrl}
                    emailContent={selectedCampaign.emailContent}
                    attachments={selectedCampaign.attachments}
                    toggleModal={() => setShowDuplicateDialog(false)}
                    setSelectedAction={setSelectedAction}
                    setCurrentSidebar={setCurrentSidebar}
                />
            }
        </CardTableShell>
    );
}
